import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { adminUserService } from '../../utils/adminUserService';
import { studioService } from '../../utils/studioService';
import AdminManagementSection from './components/AdminManagementSection';
import SuspensionModal from './components/SuspensionModal';

const AdminUserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userStats, setUserStats] = useState({
    total: 0,
    active: 0,
    admins: 0,
    premium: 0
  });
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [suspendingUser, setSuspendingUser] = useState(null);

  // Load users and stats
  const loadUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {
        role: roleFilter,
        status: statusFilter,
        search: searchTerm
      };

      const [usersResult, statsResult] = await Promise.all([
        adminUserService.getUsers(filters),
        adminUserService.getUserStats()
      ]);

      if (usersResult.error) {
        throw new Error(usersResult.error);
      }

      if (statsResult.error) {
        throw new Error(statsResult.error);
      }

      setUsers(usersResult.data);
      setUserStats(statsResult.data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [roleFilter, statusFilter]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        loadUsers();
      } else if (searchTerm === '') {
        loadUsers();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Apply client-side subscription filtering
  const filteredUsers = users.filter(user => {
    if (subscriptionFilter === 'all') return true;
    return user.subscription_status === subscriptionFilter;
  });

  const handleUserAction = async (action, userId, reason = null) => {
    setActionLoading(true);
    try {
      let result;
      switch (action) {
        case 'suspend':
          if (!reason) {
            // Show suspension modal to get reason
            const user = users.find(u => u.id === userId);
            setSuspendingUser(user);
            setShowSuspensionModal(true);
            setActionLoading(false);
            return;
          }
          result = await adminUserService.updateUserStatus(userId, 'suspended', reason);
          setSuccessMessage('User suspended successfully');
          break;
        case 'activate':
          result = await adminUserService.updateUserStatus(userId, 'active');
          setSuccessMessage('User activated successfully');
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            result = await adminUserService.deleteUser(userId);
            setSuccessMessage('User deleted successfully');
          } else {
            setActionLoading(false);
            return;
          }
          break;
        case 'promote':
          result = await adminUserService.promoteToAdmin(userId);
          setSuccessMessage('User promoted to admin successfully');
          break;
        case 'demote':
          result = await adminUserService.demoteFromAdmin(userId);
          setSuccessMessage('Admin demoted successfully');
          break;
        default:
          console.log(`${action} user:`, userId);
          return;
      }

      if (result && result.error) {
        throw new Error(result.error);
      }

      // Reload users after action
      await loadUsers();
      setSelectedUsers([]);
    } catch (err) {
      setError(err.message);
      console.error(`Error ${action} user:`, err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) return;

    setActionLoading(true);
    try {
      const result = await adminUserService.bulkUpdateUsers(selectedUsers, action);

      if (result.error) {
        throw new Error(result.error);
      }

      // Reload users after bulk action
      await loadUsers();
      setSelectedUsers([]);
    } catch (err) {
      setError(err.message);
      console.error(`Error bulk ${action}:`, err);
    } finally {
      setActionLoading(false);
    }
  };

  const UserModal = ({ user: editUser, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      full_name: editUser?.full_name || '',
      email: editUser?.email || '',
      role: editUser?.role || 'user',
      password: ''
    });
    const [saving, setSaving] = useState(false);
    const [studios, setStudios] = useState([]);
    const [selectedStudioId, setSelectedStudioId] = useState('');
    const [studioAction, setStudioAction] = useState('link'); // 'link' or 'create'
    const [newStudioName, setNewStudioName] = useState('');
    const [loadingStudios, setLoadingStudios] = useState(false);

    // Load studios when role changes to studio_admin
    useEffect(() => {
      if (formData.role === 'studio_admin' && studios.length === 0) {
        loadStudios();
      }
    }, [formData.role]);

    const loadStudios = async () => {
      setLoadingStudios(true);
      const { data } = await studioService.getAllStudios();
      setStudios(data || []);
      setLoadingStudios(false);
    };

    const handleSave = async () => {
      setSaving(true);
      try {
        let result;
        if (editUser) {
          // Update existing user
          result = await adminUserService.updateUser(editUser.id, formData);

          // If changing to studio_admin, handle studio assignment
          if (formData.role === 'studio_admin' && editUser.role !== 'studio_admin') {
            if (studioAction === 'link' && selectedStudioId) {
              // Link to existing studio
              await studioService.linkStudioToUser(selectedStudioId, editUser.id, user.id);
            } else if (studioAction === 'create' && newStudioName) {
              // Create new studio
              await studioService.createStudioWithOwner(
                { name: newStudioName, status: 'active' },
                editUser.email
              );
            }
          }
        } else {
          // Create new user
          result = await adminUserService.createUser(formData);
        }

        if (result.error) {
          throw new Error(result.error);
        }

        await loadUsers();
        onSave();
      } catch (err) {
        setError(err.message);
        console.error('Error saving user:', err);
      } finally {
        setSaving(false);
      }
    };

    // Check if studio assignment is needed
    const showStudioAssignment = formData.role === 'studio_admin' &&
      editUser && editUser.role !== 'studio_admin';

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {editUser ? 'Edit User' : 'Add User'}
            </h3>
            <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
          </div>

          <div className="space-y-4">
            <Input
              label="Full Name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="Enter full name"
            />
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter email address"
              disabled={!!editUser} // Can't change email for existing users
            />
            {!editUser && (
              <Input
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password (optional - will use default if empty)"
              />
            )}
            <Select
              label="Role"
              value={formData.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
              options={[
                { value: 'user', label: 'User' },
                { value: 'studio_admin', label: 'Studio Admin' },
                { value: 'admin', label: 'Admin' }
              ]}
            />

            {/* Studio Assignment Section */}
            {showStudioAssignment && (
              <div className="border border-border rounded-lg p-4 bg-muted/20">
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center">
                  <Icon name="Building" size={16} className="mr-2" />
                  Studio Assignment
                </h4>

                <div className="flex space-x-2 mb-3">
                  <button
                    onClick={() => setStudioAction('link')}
                    className={`flex-1 py-2 px-3 text-sm rounded-lg border transition-colors ${studioAction === 'link'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-muted'
                      }`}
                  >
                    Link Existing
                  </button>
                  <button
                    onClick={() => setStudioAction('create')}
                    className={`flex-1 py-2 px-3 text-sm rounded-lg border transition-colors ${studioAction === 'create'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-muted'
                      }`}
                  >
                    Create New
                  </button>
                </div>

                {studioAction === 'link' ? (
                  loadingStudios ? (
                    <p className="text-sm text-muted-foreground">Loading studios...</p>
                  ) : (
                    <Select
                      label="Select Studio"
                      value={selectedStudioId}
                      onChange={(value) => setSelectedStudioId(value)}
                      options={[
                        { value: '', label: 'Choose a studio...' },
                        ...studios.map(s => ({
                          value: s.id,
                          label: `${s.name}${s.owner ? ` (owned by ${s.owner.email})` : ''}`
                        }))
                      ]}
                    />
                  )
                ) : (
                  <Input
                    label="Studio Name"
                    value={newStudioName}
                    onChange={(e) => setNewStudioName(e.target.value)}
                    placeholder="Enter new studio name"
                  />
                )}

                {studioAction === 'link' && selectedStudioId && (() => {
                  const selectedStudio = studios.find(s => s.id === selectedStudioId);
                  if (selectedStudio?.owner) {
                    return (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                        <Icon name="AlertTriangle" size={12} className="inline mr-1" />
                        This will transfer ownership from {selectedStudio.owner.email}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border lg:hidden">
          <div className="px-4 py-4">
            <h1 className="text-xl font-bold text-foreground">User Management</h1>
          </div>
        </div>

        <main className="p-4 lg:p-8 pt-20 lg:pt-8 pb-20 lg:pb-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={20} className="text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setError(null)}
                  className="ml-auto"
                />
              </div>
            </div>
          )}

          {/* Success Display */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="CheckCircle" size={20} className="text-green-600 mr-2" />
                <p className="text-green-800">{successMessage}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setSuccessMessage(null)}
                  className="ml-auto"
                />
              </div>
            </div>
          )}
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                User Management
              </h1>
              <p className="text-muted-foreground">
                Manage user accounts, roles, and permissions
              </p>
            </div>

            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={() => console.log('Export users')}
              >
                Export
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                onClick={() => setShowUserModal(true)}
              >
                Add User
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold text-foreground">{userStats.total}</p>
                </div>
                <Icon name="Users" size={24} className="text-primary" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Users</p>
                  <p className="text-2xl font-bold text-foreground">{userStats.active}</p>
                </div>
                <Icon name="UserCheck" size={24} className="text-green-500" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Premium Users</p>
                  <p className="text-2xl font-bold text-foreground">{userStats.premium}</p>
                </div>
                <Icon name="Crown" size={24} className="text-purple-500" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Trials</p>
                  <p className="text-2xl font-bold text-foreground">{userStats.trialing || 0}</p>
                </div>
                <Icon name="Clock" size={24} className="text-blue-500" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Expired Trials</p>
                  <p className="text-2xl font-bold text-foreground">{userStats.trial_expired || 0}</p>
                </div>
                <Icon name="AlertTriangle" size={24} className="text-red-500" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Admins</p>
                  <p className="text-2xl font-bold text-foreground">{userStats.admins}</p>
                </div>
                <Icon name="Shield" size={24} className="text-orange-500" />
              </div>
            </div>
          </div>

          {/* Admin Management Section */}
          <AdminManagementSection
            onError={setError}
            onSuccess={setSuccessMessage}
          />

          {/* Filters and Search */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
              <div className="flex-1">
                <Input
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  iconName="Search"
                />
              </div>

              <div className="flex space-x-3">
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'All Roles' },
                    { value: 'user', label: 'Users' },
                    { value: 'studio_admin', label: 'Studio Admins' },
                    { value: 'admin', label: 'Admins' }
                  ]}
                />

                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'active', label: 'Active' },
                    { value: 'suspended', label: 'Suspended' },
                    { value: 'banned', label: 'Banned' },
                    { value: 'inactive', label: 'Inactive' }
                  ]}
                />

                <Select
                  value={subscriptionFilter}
                  onChange={(e) => setSubscriptionFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'All Subscriptions' },
                    { value: 'active', label: 'Active Subscriptions' },
                    { value: 'trialing', label: 'Active Trials' },
                    { value: 'trial_expired', label: 'Expired Trials' },
                    { value: 'past_due', label: 'Past Due' },
                    { value: 'canceled', label: 'Canceled' },
                    { value: 'none', label: 'No Subscription' }
                  ]}
                />
              </div>
            </div>

            {selectedUsers.length > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <span className="text-sm text-muted-foreground">
                  {selectedUsers.length} user(s) selected
                </span>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // For bulk suspension, we'll use a generic reason
                      handleBulkAction('suspend');
                    }}
                    disabled={actionLoading}
                  >
                    Suspend
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkAction('activate')}
                    disabled={actionLoading}
                  >
                    Activate
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleBulkAction('delete')}
                    disabled={actionLoading}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Users Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="text-left p-4">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers(filteredUsers.map(u => u.id));
                          } else {
                            setSelectedUsers([]);
                          }
                        }}
                        className="rounded border-border"
                      />
                    </th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">User</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Role</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Subscription</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Activity</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-muted-foreground">
                        <div className="flex items-center justify-center">
                          <Icon name="Loader2" size={20} className="animate-spin mr-2" />
                          Loading users...
                        </div>
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-muted-foreground">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="border-t border-border hover:bg-muted/20">
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers([...selectedUsers, user.id]);
                              } else {
                                setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                              }
                            }}
                            className="rounded border-border"
                          />
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                              <Icon name="User" size={16} className="text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{user.full_name}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                              user.role === 'studio_admin' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                            }`}>
                            {user.role.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-col">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                user.status === 'suspended' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                  user.status === 'banned' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                    user.status === 'inactive' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' :
                                      'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                              }`}>
                              {user.status}
                            </span>
                            {user.status_reason && (
                              <span className="text-xs text-muted-foreground mt-1" title={user.status_reason}>
                                {user.status_reason.length > 30 ? `${user.status_reason.substring(0, 30)}...` : user.status_reason}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-col">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.subscription_status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                user.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                  user.subscription_status === 'trial_expired' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                    user.subscription_status === 'past_due' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                      user.subscription_status === 'canceled' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' :
                                        user.subscription_status === 'unpaid' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                          'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                              }`}>
                              {user.subscription_status === 'trial_expired' ? 'Trial Expired' :
                                user.subscription_status === 'trialing' ? 'Trialing' :
                                  user.subscription_status === 'active' ? 'Active' :
                                    user.subscription_status === 'past_due' ? 'Past Due' :
                                      user.subscription_status === 'canceled' ? 'Canceled' :
                                        user.subscription_status === 'unpaid' ? 'Unpaid' :
                                          user.subscription_status === 'none' ? 'No Subscription' :
                                            user.subscription_status}
                            </span>
                            {user.subscription_details?.isTrialExpired && (
                              <span className="text-xs text-red-600 dark:text-red-400 mt-1">
                                Expired {Math.abs(user.subscription_details.daysUntilExpiry)} days ago
                              </span>
                            )}
                            {user.subscription_status === 'trialing' && user.subscription_details?.daysUntilExpiry > 0 && (
                              <span className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                {user.subscription_details.daysUntilExpiry} days left
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            <p className="text-foreground">{user.games_played} games</p>
                            <p className="text-muted-foreground">{user.total_playtime}min played</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Edit"
                              onClick={() => {
                                setEditingUser(user);
                                setShowUserModal(true);
                              }}
                              disabled={actionLoading}
                            />
                            {user.role !== 'admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Shield"
                                onClick={() => handleUserAction('promote', user.id)}
                                disabled={actionLoading}
                                title="Promote to Admin"
                              />
                            )}
                            {user.role === 'admin' && user.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="ShieldOff"
                                onClick={() => handleUserAction('demote', user.id)}
                                disabled={actionLoading}
                                title="Demote from Admin"
                              />
                            )}
                            {user.status === 'active' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="UserX"
                                onClick={() => handleUserAction('suspend', user.id)}
                                disabled={actionLoading}
                                title="Suspend User"
                              />
                            ) : user.status === 'suspended' || user.status === 'banned' || user.status === 'inactive' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="UserCheck"
                                onClick={() => handleUserAction('activate', user.id)}
                                disabled={actionLoading}
                                title="Activate User"
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {showUserModal && (
        <UserModal
          user={editingUser}
          onClose={() => {
            setShowUserModal(false);
            setEditingUser(null);
          }}
          onSave={() => {
            setShowUserModal(false);
            setEditingUser(null);
            // Implement save logic
          }}
        />
      )}

      {showSuspensionModal && (
        <SuspensionModal
          user={suspendingUser}
          onClose={() => {
            setShowSuspensionModal(false);
            setSuspendingUser(null);
          }}
          onSuspend={(reason) => {
            setShowSuspensionModal(false);
            handleUserAction('suspend', suspendingUser.id, reason);
            setSuspendingUser(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminUserManagement;
